import { zodResolver } from '@hookform/resolvers/zod'
import { useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { mainBalance } from '~/entities/MainBalance'
import { priceRequiredScheme } from '~/shared/lib/schemas'
import { parseSumForUi, sanitizePriceNumber } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import { DrawerComponent } from '~/shared/ui/Drawer'
import { FormProvider, NumericInput } from '~/shared/ui/form'
import { myPalette } from '~/shared/ui/theme'
import {
  $debt,
  $isRepayDrawerOpen,
  closeRepayDrawer,
  repay,
  repayFx,
} from '../../model'

const amountInputParams = {
  precision: 2,
  decimalSeparator: ',',
  decimalPseudoSeparators: ['.'],
  thousandSeparator: ' ',
  min: 0.01,
}

const repaySchema = z.object({
  amount: priceRequiredScheme,
})
type FormValues = z.infer<typeof repaySchema>

export function RepayDrawer() {
  const isOpen = useStore($isRepayDrawerOpen)
  const isSubmitting = useStore(repayFx.pending)

  const debtBalance = useStoreMap($debt, (d) => d?.getBalance() ?? 0)
  const mainBalanceAmount = useStoreMap(
    mainBalance.$currentBalance,
    (b) => b?.getAmount() ?? 0,
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(repaySchema),
  })

  const amountForm = form.watch('amount')

  const amountError = useMemo(() => {
    const amount = sanitizePriceNumber(amountForm || 0)
    if (amount <= 0) return undefined
    if (amount > debtBalance) {
      return `Превышает остаток задолженности — ${parseSumForUi(debtBalance)}`
    }
    if (amount > mainBalanceAmount) {
      return `Недостаточно средств на основном балансе — доступно ${parseSumForUi(
        mainBalanceAmount,
      )}`
    }
    return undefined
  }, [amountForm, debtBalance, mainBalanceAmount])

  const handleRepayAll = () => {
    const maxAmount = Math.min(debtBalance, mainBalanceAmount)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    form.reset({ amount: String(maxAmount).replace('.', ',') })
  }

  const handleSubmit = () => {
    if (amountError) return
    const amount = sanitizePriceNumber(form.getValues('amount') || 0)
    repay(amount)
  }

  const handleClose = () => {
    form.reset()
    closeRepayDrawer()
  }

  return (
    <DrawerComponent isShow={isOpen} onCancel={handleClose}>
      <div style={{ marginBottom: '8px', fontSize: '20px', fontWeight: 700 }}>
        Досрочное погашение
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '14px',
          color: myPalette.mutedText.main,
          marginBottom: '4px',
        }}
      >
        <span>Остаток задолженности</span>
        <span>{parseSumForUi(debtBalance)}</span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '14px',
          color: myPalette.mutedText.main,
          marginBottom: '24px',
        }}
      >
        <span>Баланс</span>
        <span>{parseSumForUi(mainBalanceAmount)}</span>
      </div>

      <FormProvider form={form} onSuccess={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <NumericInput
            name='amount'
            label='Сумма для погашения'
            errorText={amountError}
            numberParams={amountInputParams}
          />
          <Button
            variant='textLink'
            onClick={handleRepayAll}
            type='button'
            style={{ marginTop: '15px' }}
          >
            Погасить полностью
          </Button>
        </div>

        <Button
          fullWidth
          type='submit'
          variant='limeFlooded'
          size='large'
          loading={isSubmitting}
          disabled={Boolean(amountError)}
        >
          Оплатить
        </Button>
      </FormProvider>
    </DrawerComponent>
  )
}
